import { levenbergMarquardt } from '@mytskine/curve-fitting'

const paramNames = ['Vmax', 'K', 'n']
const epsilon = 1e-9 // Small value
// --- Helper Functions ---
/**
 * Parameterized Hill function generator.
 * Takes an array of parameters [Vmax, K, n] and returns a function (x) => y.
 */
const hillFunctionGenerator = (p) => {
  if (!p || p.length !== 3 || p.some(val => !isFinite(val))) {
    console.error('INVALID params in hillFunctionGenerator:', p)
    return (x) => NaN
  }
  const [Vmax, K, n] = p
  const KSafe = Math.max(K, epsilon)
  const nSafe = n
  return (x) => {
    const xSafe = Math.max(x, epsilon)
    let result = NaN
    try {
      const KPowN = Math.pow(KSafe, nSafe)
      const xPowN = Math.pow(xSafe, nSafe)
      if (!isFinite(KPowN) || !isFinite(xPowN)) {
        result = NaN
      } else {
        const denominator = KPowN + xPowN
        if (denominator === 0 || !isFinite(denominator)) {
          result = NaN
        } else {
          result = Vmax * xPowN / denominator
          if (!isFinite(result)) {
            result = NaN
          }
        }
      }
    } catch (error) {
      console.error('Error during Hill calculation (inner):', error, 'x:', x, 'Params:', p)
      result = NaN
    }
    return result
  }
}

/**
 * Calculates the Root Mean Squared Error, ignoring non-finite points.
 */
const calculateRmse = (yTrue, yPred, p) => {
  if (yTrue.length !== yPred.length) {
    throw new Error('Input arrays must have the same length for RMSE')
  }
  let sumOfSquares = 0
  let validPoints = 0
  for (let i = 0; i < yTrue.length; i++) {
    if (isFinite(yTrue[i]) && isFinite(yPred[i])) {
      const error = yTrue[i] - yPred[i]
      sumOfSquares += error * error
      validPoints++
    }
  }
  if (validPoints === 0) {
    console.error('No valid points found for RMSE calculation. Params:', p)
    return Infinity
  }
  return Math.sqrt(sumOfSquares / validPoints)
}

/**
 * Finds the median of a sorted array.
 */
const median = (sortedArr) => {
  const mid = Math.floor(sortedArr.length / 2)
  if (sortedArr.length === 0) return 0
  if (sortedArr.length % 2 !== 0) {
    return sortedArr[mid]
  } else {
    return (sortedArr[mid - 1] + sortedArr[mid]) / 2
  }
}

// --- Fitting Logic ---
/**
 * Fits the Hill saturation model using the @mytskine/curve-fitting library.
 *
 * Exported so the EKRM wizards' saturation sliders (src/utils/hillCurve.js) recover the same
 * parameters this file's CSV export writes out. calculateHillParameters keeps using it unchanged.
 */
export const fitSaturationCurve = (dataPoints) => {
  const xData = dataPoints.map(p => p[0])
  const yData = dataPoints.map(p => p[1])
  const data = { x: xData, y: yData }

  // --- Initial Parameter Guesses (Heuristics) ---
  let yMaxGuess = Math.max(...yData)
  if (!isFinite(yMaxGuess) || yMaxGuess <= 0) {
    yMaxGuess = 1.0
  }
  let minDist = Infinity
  let halfMaxYIdx = 0
  yData.forEach((y, i) => {
    const dist = Math.abs(y - yMaxGuess / 2)
    if (dist < minDist) {
      minDist = dist
      halfMaxYIdx = i
    }
  })
  let xHalfMaxGuess = xData[halfMaxYIdx]
  if (xHalfMaxGuess === 0) {
    const positiveX = xData.filter(x => x > 0).sort((a, b) => a - b)
    xHalfMaxGuess = positiveX.length > 0 ? median(positiveX) : 1.0
  }
  if (xHalfMaxGuess <= 0 || !isFinite(xHalfMaxGuess)) {
    xHalfMaxGuess = epsilon
  }
  const initialGuess = [
    isFinite(yMaxGuess) ? yMaxGuess : 1.0,
    isFinite(xHalfMaxGuess) ? xHalfMaxGuess : epsilon,
    1.5
  ]
  // console.log('--- Fitting Hill Saturation Model (JS - @mytskine/curve-fitting) ---')
  // console.log('Initial Guess (Vmax, K, n):', initialGuess.map(v => v.toExponential(3)))

  const options = {
    parameters: {
      initial: initialGuess // Pass initial guess array here
    },
    // Add other options if needed and confirmed by library docs/examples
    // e.g., maxIterations: 1000, timeout: 10 (seconds?)
    maxIterations: 1000
    // Check documentation for how bounds are handled - might not be in options
  }
  // console.log('Using Options:', options)

  let result = { params: null, rmse: Infinity, paramNames: paramNames, error: null, iterations: 0 }

  try {
    // console.log('Calling @mytskine/curve-fitting levenbergMarquardt...')
    // Pass the data, the function generator, and the options object
    const fitted = levenbergMarquardt(data, hillFunctionGenerator, options)
    // console.log('...@mytskine/curve-fitting call finished.')
    // console.log('Fitted object returned:', fitted) // Log the raw output

    // Process results
    if (fitted && fitted.parameters && fitted.parameters.every(isFinite)) { // Check for fitted.parameters
      result.params = fitted.parameters // Assign from fitted.parameters
      const finalPredictionFunction = hillFunctionGenerator(result.params)
      const yPred = data.x.map(finalPredictionFunction)

      result.rmse = calculateRmse(data.y, yPred, result.params)
      // Check for iterations and error in the returned object
      result.iterations = fitted.iterations !== undefined ? fitted.iterations : 'N/A'
      result.libraryError = fitted.finalError !== undefined ? fitted.finalError : 'N/A' // Assuming it might be called finalError

      if (!isFinite(result.rmse)) {
        throw new Error(`RMSE calculation resulted in non-finite value. Params: ${result.params.map(v => v.toExponential(3))}`)
      }

      // console.log('  Success!')
      // console.log(`  Parameters (${paramNames.join(', ')}):`, result.params.map(p => p.toExponential(4)).join(', '))
      // console.log(`  RMSE: ${result.rmse.toExponential(4)}`)
      if (result.libraryError !== 'N/A') console.log(`  Library Final Error (SSE?): ${result.libraryError.toExponential(4)}`)
      // console.log(`  Iterations: ${result.iterations}`)
    } else {
      let errorMsg = '@mytskine/curve-fitting optimization failed or returned invalid parameters.'
      if (fitted) {
        if (fitted.status) errorMsg += ` Status: ${fitted.status}.` // Check for status message
        if (fitted.parameters) errorMsg += ` Resulting Params: ${fitted.parameters}.`
        else errorMsg += ` Fitted object keys: ${Object.keys(fitted)}`
      } else {
        errorMsg += ' Fitted object is null or undefined.'
      }
      console.error(errorMsg, 'Fitted object details:', fitted)
      throw new Error(errorMsg)
    }
  } catch (e) {
    console.error('  Fitting Error Caught:', e)
    result.error = e.message || 'Unknown fitting error during LM execution.'
    if (e.stack) {
      console.error('Stack Trace:', e.stack)
    }
    result.params = null
    result.rmse = Infinity
  }
  return result
}

export const saturationCurves = {
  methods: {
    calculateHillParameters (data) {
      let results = []
      for (const channel of Object.keys(data)) {
        const xArray = data[channel].x
        const yArray = data[channel].y
        const formattedData = xArray.map((x, index) => [x, yArray[index]])
        const fittingResult = fitSaturationCurve(formattedData)
        if (fittingResult.params && fittingResult.params.every(isFinite) && isFinite(fittingResult.rmse)) {
          /* console.log(`Iterations: ${fittingResult.iterations}`)
          if (fittingResult.libraryError !== 'N/A' && isFinite(fittingResult.libraryError)) {
            console.log(`Library Final Error (SSE?): ${fittingResult.libraryError.toExponential(4)}`)
          }
          console.log(`Calculated RMSE: ${fittingResult.rmse.toExponential(4)}`)
          console.log(`Fitted Parameters:`)
          fittingResult.paramNames.forEach((name, i) => {
            console.log(`  ${name}: ${fittingResult.params[i].toExponential(5)}`)
          })
          console.log(`\nParameter Interpretations:`) */
          const [Vmax, K, n] = fittingResult.params
          results.push(`${channel}\n`)
          results.push(`carry_cap,"${Vmax.toFixed(4)}"\n`)
          results.push(`shape_u,"${K.toFixed(4)}"\n`)
          results.push(`shape_b,"${n.toFixed(4)}"\n`)
          /* console.log(`  Vmax = ${Vmax.toFixed(4)} - The maximum response level`)
          console.log(`  K    = ${K.toFixed(4)} - The input level for half-max response`)
          console.log(`  n    = ${n.toFixed(4)} - The Hill coefficient (${n > 1 ? 'positive cooperativity' : n < 1 ? 'negative cooperativity' : 'no cooperativity'})`) */
        } else {
          console.log(`Status: Failed`)
          console.log(`Error: ${fittingResult.error || 'Fitting produced non-finite results.'}`)
          if (fittingResult.params) {
            console.log(`Resulting Params (potentially invalid): ${fittingResult.params}`)
          }
        }
      }
      // Convert array to CSV string
      const csvContent = results.map(value => value).join('')
      // Create a Blob object
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)

      // Create a download link and simulate a click
      const link = document.createElement('a')
      link.setAttribute('href', url)
      link.setAttribute('download', `Curves.csv`)
      document.body.appendChild(link)
      link.click()

      // Clean up
      document.body.removeChild(link)
    }
  }
}
